import { Check } from 'lucide-react';
import { IDEBadge } from '@/components/shared/extensions';
import {
  RISK_ESTIMATE_STATE_FILTER_VALUES,
  type ExtensionSource,
} from '@kirin/api-client/contracts/extensions';
import { SourceCell, type SourceType } from '@/components/shared/extensions';
import {
  formatRiskLabel,
  riskLabelSeverity,
  RiskEstimateCell,
} from '@/components/shared/extensions';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';

interface FilterOptionProps {
  value: string;
  isSelected: boolean;
}

export function ExtensionSourceFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      <SourceCell source={value as SourceType} showTooltip={false} />
    </div>
  );
}

export function ExtensionIDEFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      <IDEBadge sources={[value as ExtensionSource]} />
    </div>
  );
}

/**
 * Risk Estimate multiselect option (KIR-1928): the raw AgentMesh label styled
 * like the table cell (best-effort severity color, neutral for unknown vocab).
 * The label-less states render exactly as their cells do (KIR-4726).
 */
export function RiskEstimateFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  const isNotAssessed =
    value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_ASSESSED;
  const isBuiltIn =
    value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN;
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      {isNotAssessed || isBuiltIn ? (
        <span className="text-xs">
          <RiskEstimateCell riskEstimate={null} isBuiltIn={isBuiltIn} />
        </span>
      ) : (
        <SeverityBadge
          riskLevel={riskLabelSeverity(value)}
          label={formatRiskLabel(value)}
          showIcon={false}
        />
      )}
    </div>
  );
}
